import { ChangeDetectionStrategy, Component, computed, effect, inject, model, output } from '@angular/core'
import { FormControl, FormGroup, FormsModule, NgControl, ReactiveFormsModule, Validators } from '@angular/forms'
import { ControlValueAccessor } from '../../modules/form/core/control-value-accessor'
import { ErrorMessageResolver } from '../../modules/form/errors/error-message-resolver'
import { MatDialog } from '@angular/material/dialog'
import { ShopSettingsService } from '../../../modules/auth/shop-settings.service'
import { UserTermsDialog } from '../../../modules/user-terms/user-terms.dialog'
import { FmxFormSubmitDirective } from '../../modules/form/submit/form-submit.directive'
import { InputComponent } from '../../modules/form/input/input.component'
import { FmxControlSubmitDirective } from '../../modules/form/submit/control-submit.directive'
import { CartOrderItemsFormComponent } from '../cart-order-item-form/cart-order-items-form.component'
import { MatCheckbox } from '@angular/material/checkbox'
import { MatError } from '@angular/material/select'
import { FmxErrorsComponent } from '../../modules/form/errors/errors.component'
import { TranslatePipe } from '@ngx-translate/core'
import { CartOrder } from '../../../modules/dashboard/shop/services/cart-order.type'
import { ShopService } from '../../../core/services/shop.service'
import { CartService } from '../../../modules/dashboard/shop/services/cart.service'
import { FormGroupValue } from '../../modules/form/core/form-types'

@Component({
	selector: 'app-cart-order-form',
	templateUrl: './cart-order-form.component.html',
	styleUrls: ['./cart-order-form.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		ReactiveFormsModule,
		FmxFormSubmitDirective,
		InputComponent,
		FmxControlSubmitDirective,
		CartOrderItemsFormComponent,
		MatCheckbox,
		MatError,
		FmxErrorsComponent,
		TranslatePipe,
		FormsModule,
	],
})
export class CartOrderFormComponent extends ControlValueAccessor<CartOrder> {
	protected readonly ngControl: NgControl
	private readonly dialog = inject<MatDialog>(MatDialog)

	readonly shopService = inject(ShopService)
	readonly shopSettingsService = inject(ShopSettingsService)
	readonly cartService = inject(CartService)

	readonly conditionsAgree = output<boolean>()

	readonly control = new FormGroup<FormGroupValue<CartOrder>>({
		uid: new FormControl<string>('', { nonNullable: true }),
		customer: new FormControl<string | null>(null, [Validators.required]),
		phone: new FormControl<string | null>(null, [Validators.required, Validators.pattern('^[+0-9][0-9]*$')]),
		address: new FormControl<string | null>(null),
		city: new FormControl<string | null>(null),
		county: new FormControl<string | null>(null),
		email: new FormControl<string | null>(null),
		items: new FormControl<CartOrder['items']>([], Validators.required),
	})
	readonly emailControl = this.control.controls.email
	readonly addressControl = this.control.controls.address
	readonly cityControl = this.control.controls.city
	readonly countyControl = this.control.controls.county

	readonly agreed = model<boolean>(false)
	readonly termsAccepted = model<boolean>(false)

	readonly conditionsAgreed = computed(() => this.agreed() && this.termsAccepted())

	readonly conditionsAgreementValidationResolver: ErrorMessageResolver = {
		errorKey: 'required',
		getMessage: () => 'Pentru a plasa comanda trebuie sa bifati casutele de confirmare',
	}

	readonly itemsLength = computed(() => this.controlValue()?.items.length ?? 0)

	constructor() {
		const ngControl = inject<NgControl>(NgControl)
		super(ngControl)
		this.ngControl = ngControl

		effect(() => {
			if (this.conditionsAgreed()) {
				this.conditionsAgree.emit(true)
			} else {
				this.conditionsAgree.emit(false)
			}
		})

		effect(() => {
			const isPublicShop = this.shopService.isPublicShop()

			this.addressControl.setValidators(isPublicShop ? [Validators.required] : null)
			this.addressControl.updateValueAndValidity()

			this.cityControl.setValidators(isPublicShop ? [Validators.required] : null)
			this.cityControl.updateValueAndValidity()

			this.countyControl.setValidators(isPublicShop ? [Validators.required] : null)
			this.countyControl.updateValueAndValidity()

			this.emailControl.setValidators(isPublicShop ? [Validators.required, Validators.email] : null)
			this.emailControl.updateValueAndValidity()
		})
	}

	openTermsAndConditions(event: Event) {
		event.stopPropagation()
		const dialogRef = this.dialog.open(UserTermsDialog, {
			data: undefined,
			position: { top: '2%' },
			width: '700px',
			disableClose: true,
		})
		dialogRef.afterClosed().subscribe()
	}
}
